import logo from "../assets/Logo.png";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaBars, FaTimes } from "react-icons/fa";

function Navbar() {
  const [activeSection, setActiveSection] = useState("heroSection");
  const [isServicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const toggleServicesDropdown = () => {
    setServicesDropdownOpen(!isServicesDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
    setServicesDropdownOpen(false); // Close services dropdown when toggling mobile menu
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navItems = [
    { id: "heroSection", label: "Home", path: "/" },
    { id: "history", label: "About us", path: "/about" },
    { id: "services", label: "Our Services", icon: <FaChevronDown /> },
    { id: "work", label: "Our Work", path: "/work" },
    { id: "contact", label: "Contact Us", path: "/contact" },
  ];

  const serviceItems = [
    {
      id: "webdevelopment",
      label: "Website Development",
      path: "/webdevelopment",
    },
    { id: "android", label: "iOS/Android Development", path: "/android" },
    { id: "marketing", label: "Digital Marketing", path: "/marketing" },
    { id: "uxui", label: "Ui/Ux Design", path: "/uxui" },
  ];

  const handleDesktopNavigation = (item) => {
    navigate(item.path); // Direct navigation for mobile
    setActiveSection(item.id);
    setServicesDropdownOpen(false); // Close services dropdown
    setMobileMenuOpen(false); // Close mobile menu
  };

  const handleMobileNavigation = (item) => {
    navigate(item.path); // Direct navigation for mobile
    setActiveSection(item.id);
    setServicesDropdownOpen(false); // Close services dropdown
    setMobileMenuOpen(false); // Close mobile menu
  };

  return (
    <nav className="bg-gray-100 border-gray-200 sticky top-0 z-50 shadow-lg">
      <div className="mx-auto flex items-center justify-between py-4 px-4 md:px-10">
        {/* Logo */}
        <div
          className="flex items-center cursor-pointer"
          onClick={() => navigate("/")}
        >
          <img src={logo} className="h-10" alt="Logo" />
        </div>

        {/* Hamburger Menu Button (visible on mobile) */}
        <button
          className="md:hidden text-gray-900 hover:text-orange-500 focus:outline-none"
          onClick={toggleMobileMenu}
        >
          {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>

        {/* Desktop Menu */}
        <div className="hidden md:flex md:items-center">
          {navItems.map((item) =>
            item.id !== "services" ? (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => handleDesktopNavigation(item)}
                className={`text-lg flex items-center px-4 py-2 ${
                  location.pathname === item.path
                    ? "text-orange-500"
                    : "text-gray-900 hover:text-orange-500"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <div
                key={item.id}
                className="relative flex items-center"
                ref={dropdownRef}
              >
                <button
                  onClick={toggleServicesDropdown}
                  className={`text-lg flex items-center px-4 py-2 ${
                    isServicesDropdownOpen
                      ? "text-orange-500"
                      : "text-gray-900 hover:text-orange-500"
                  }`}
                >
                  {item.label}{" "}
                  <span className="text-base ml-2 mt-1 text-gray-500">
                    {item.icon}
                  </span>
                </button>
                {isServicesDropdownOpen && (
                  <div className="absolute bg-white border rounded top-12 shadow-lg w-64 p-2">
                    {serviceItems.map((service) => (
                      <Link
                        key={service.id}
                        to={service.path}
                        onClick={() => handleDesktopNavigation(service)}
                        className={`block px-4 py-3 text-gray-700 hover:text-orange-500 ${
                          location.pathname === service.path
                            ? "text-orange-500"
                            : ""
                        }`}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-100 px-4 py-2">
          {navItems.map((item) =>
            item.id !== "services" ? (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => handleMobileNavigation(item)}
                className={`block py-3 px-4 text-lg ${
                  location.pathname === item.path
                    ? "text-orange-500"
                    : "text-gray-900 hover:text-orange-500"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <div key={item.id} className="relative">
                <button
                  onClick={toggleServicesDropdown}
                  className={`w-full text-lg flex items-center justify-between py-3 px-4 ${
                    isServicesDropdownOpen
                      ? "text-orange-500"
                      : "text-gray-900 hover:text-orange-500"
                  }`}
                >
                  {item.label}{" "}
                  <FaChevronDown className="text-base text-gray-500" />
                </button>
                <div
                  className={`w-full overflow-hidden transition-all duration-300 ${
                    isServicesDropdownOpen ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="bg-white border rounded shadow-lg w-full">
                    {serviceItems.map((service) => (
                      <Link
                        key={service.id}
                        to={service.path}
                        onClick={() => {
                          handleMobileNavigation(service);
                          setMobileMenuOpen(false); // Close mobile menu after selection
                        }}
                        className={`block px-4 py-3 text-gray-700 hover:bg-gray-200 ${
                          location.pathname === service.path
                            ? "text-orange-500"
                            : ""
                        }`}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;
